import { Device } from '../helpers'
import { domReady } from '../helpers/DOM'
import { doOnce } from '../helpers/doOnce'
import Logger from '../helpers/Logger'
import { onMobileTapClick } from '../helpers/onMobileTapClick'
import type WebModel from '../models/WebModel'
import { OUTSTREAM_REFRESH_TIME } from '../outstream/OutstreamRenderer'
import { UniversalPlayer } from '../slots'
import type { Slot } from '../slots/Slot'
import type SlotFactory from '../slots/SlotFactory'
import { VIDEO_TARGET_CLASS } from '../stickyVideo/StickyVideo'

/**
 * UniversalPlayers class
 * Responsible for displaying UniversalPlayer slots
 */
export default class UniversalPlayers {
  createdSlot: UniversalPlayer | undefined
  renderedPromise: Promise<void>

  constructor(readonly webModel: WebModel, readonly slotFactory: SlotFactory) {
    // Skip if adhesions are not turned on for this device
    if (!this.shouldDisplayUniversalPlayer()) {
      this.renderedPromise = Promise.resolve()
      return
    }
    this.renderedPromise = new Promise((resolve) => {
      domReady(() => {
        this.createAndDisplaySlot(slotFactory).then(() => {
          this.attachListenerToCTPVideos()
          resolve()
        })
      })
    })
  }

  shouldDisplayUniversalPlayer(): boolean {
    const hasAutoplayMvVideo = !!document.querySelector(
      `.${VIDEO_TARGET_CLASS}[data-autoplay=true],.${VIDEO_TARGET_CLASS}[data-automobile=true]`
    )
    const isDesktop = !Device.isMobileOrTablet
    const isDesktopEnabled = this.webModel.universal_player_desktop
    const isMobileEnabled = this.webModel.universal_player_mobile

    // Don't want to show these ads on the homepage of a site. Matches StickyVideo behavior.
    const isRoot = window.location.pathname === '/'
    return (
      ((isDesktop && isDesktopEnabled) || (!isDesktop && isMobileEnabled)) &&
      !isRoot &&
      !hasAutoplayMvVideo
    )
  }

  // We only want to run the handleCTPClick function once!
  handleCTPClick = doOnce(() => {
    this.createdSlot?.onClose()
  })

  // Attaches a click listener to every clickToPlay video on the page so that we can close the
  // UniversalPlayer when a CTP video is clicked.
  attachListenerToCTPVideos() {
    // Note: This finds every MV video on the page, but if we're in this function we
    // should have already made sure that there are no autoplay videos on the page.
    const clickToPlayVideos = document.querySelectorAll(
      `.${VIDEO_TARGET_CLASS}`
    )

    clickToPlayVideos.forEach((videoDiv) => {
      videoDiv.addEventListener('click', this.handleCTPClick)
      onMobileTapClick(videoDiv, this.handleCTPClick)
    })
  }

  setupUniversalPlayerSlot(): ISlotModel {
    const slotModel: ISlotModel = {
      adUnitId: 'universalPlayer_atf',
      type: 'UniversalPlayer' as SlotClass,
      selector: 'body',
      insertPosition: 'beforeend',
      refreshTime: OUTSTREAM_REFRESH_TIME
    }
    return slotModel
  }

  // This function actually sets up the UniversalPlayer slot on the page.
  // Called in Atf.ts.
  async createAndDisplaySlot(
    slotFactory: SlotFactory
  ): Promise<Slot | undefined> {
    try {
      const slot = (await slotFactory.new(
        this.setupUniversalPlayerSlot()
      )) as UniversalPlayer
      this.createdSlot = slot
      return slot
    } catch (e) {
      Logger.debug(e)
    }
  }
}
